import { computed, ref, nextTick } from "vue";
import { useStore } from "vuex";
import Helpers from "../../helpers";
import { mensajeError } from "../../store/plataformaStore";

const filtrosVacios = () => ({
    admin_user_id: null,
    impersonando: null,
    metodo: null,
    ruta: null,
    evento: null,
    exito: null,
    desde: null,
    hasta: null,
    q: null,
    tabla: null,
    registro_id: null,
    operacion: null,
});

export function useAuditoria() {
    const store = useStore();

    const spinner = ref(true);
    const exportando = ref(false);
    const sinAcceso = ref(false);
    const alertTable = ref({ state: false, message: "", color: "success" });
    const filtros = ref(filtrosVacios());

    // Detalle
    const modalDetalle = ref(false);
    const detalle = ref(null);
    const cargandoDetalle = ref(false);

    // Historial de un registro: { tabla, registro_id, paginador }
    const modalHistorial = ref(false);
    const historial = ref(null);
    const cargandoHistorial = ref(false);

    const showAlert = (message, color = "success", timeout = 5000) => {
        alertTable.value = { state: true, message, color };
        setTimeout(() => {
            alertTable.value.state = false;
        }, timeout);
    };

    const handleError = (error, porDefecto) => {
        if (error?.response?.status === 403) {
            sinAcceso.value = true;
            return;
        }
        showAlert(mensajeError(error, porDefecto), "danger");
        console.error("Auditoría error:", error);
    };

    // Strings vacíos de los inputs también cuentan como sin filtro
    const filtrosActivos = () => {
        const limpios = {};
        Object.entries(filtros.value).forEach(([clave, valor]) => {
            limpios[clave] = valor === "" ? null : valor;
        });
        return Helpers.eliminarNull(limpios);
    };

    // ---------- Listado ----------
    const getAuditoria = async (page = 1) => {
        if (page < 1) page = 1;
        spinner.value = true;
        try {
            await store.dispatch("appPlataforma/getAuditoriaAction", {
                filters: filtrosActivos(),
                page,
            });
        } catch (error) {
            handleError(error, "Error al cargar la auditoría");
        } finally {
            spinner.value = false;
        }
    };

    const getAdmins = async () => {
        try {
            await store.dispatch("appPlataforma/getAdminsAction");
        } catch (error) {
            handleError(error, "Error al cargar los administradores");
        }
    };

    const getTablas = async () => {
        try {
            await store.dispatch("appPlataforma/getTablasAction");
        } catch (error) {
            handleError(error, "Error al cargar las tablas");
        }
    };

    const applyFilters = async () => {
        if (filtros.value.desde && filtros.value.hasta && filtros.value.desde > filtros.value.hasta) {
            showAlert("La fecha 'desde' no puede ser posterior a 'hasta'", "warning");
            return;
        }
        store.commit("appPlataforma/setCurrentPage", 1);
        await getAuditoria(1);
    };

    const cleanFilter = () => {
        filtros.value = filtrosVacios();
        store.commit("appPlataforma/setCurrentPage", 1);
        nextTick(() => getAuditoria(1));
    };

    const pageChangeHandle = async (value) => {
        const currentPage = store.state.appPlataforma?.auditoria?.current_page || 1;
        let newPage;

        switch (value) {
            case "next":
                newPage = Math.min(currentPage + 1, auditoria.value.last_page || 1);
                break;
            case "previous":
                newPage = Math.max(currentPage - 1, 1);
                break;
            default:
                newPage = parseInt(value) || 1;
                break;
        }

        store.commit("appPlataforma/setCurrentPage", newPage);
        await getAuditoria(newPage);
    };

    const exportar = async () => {
        exportando.value = true;
        try {
            await store.dispatch("appPlataforma/exportarAuditoriaAction", filtrosActivos());
        } catch (error) {
            handleError(error, "Error al exportar la auditoría");
        } finally {
            exportando.value = false;
        }
    };

    // ---------- Detalle ----------
    const verDetalle = async (fila) => {
        detalle.value = { ...fila };
        modalDetalle.value = true;
        cargandoDetalle.value = true;
        try {
            detalle.value = await store.dispatch("appPlataforma/getAuditoriaDetalleAction", fila.id);
        } catch (error) {
            modalDetalle.value = false;
            handleError(error, "Error al cargar el detalle");
        } finally {
            cargandoDetalle.value = false;
        }
    };

    const cerrarDetalle = () => {
        modalDetalle.value = false;
        detalle.value = null;
    };

    // ---------- Historial de un registro ----------
    // Solo se aplica la respuesta de la última petición (cerrar o cambiar de registro la invalida)
    let historialPeticion = 0;
    const setHistorial = (valor) => {
        historial.value = valor;
    };

    const verHistorial = async ({ tabla, registro_id }, page = 1) => {
        const peticion = ++historialPeticion;
        const mismoRegistro = historial.value?.tabla === tabla && historial.value?.registro_id === registro_id;
        setHistorial({ tabla, registro_id, paginador: mismoRegistro ? historial.value.paginador : null });
        modalHistorial.value = true;
        cargandoHistorial.value = true;
        try {
            const paginador = await store.dispatch("appPlataforma/getHistorialAction", {
                tabla,
                registroId: registro_id,
                page,
            });
            if (peticion === historialPeticion) {
                setHistorial({ tabla, registro_id, paginador });
            }
        } catch (error) {
            if (peticion === historialPeticion) {
                modalHistorial.value = false;
                handleError(error, "Error al cargar el historial");
            }
        } finally {
            if (peticion === historialPeticion) {
                cargandoHistorial.value = false;
            }
        }
    };

    const cerrarHistorial = () => {
        historialPeticion++;
        cargandoHistorial.value = false;
        modalHistorial.value = false;
        historial.value = null;
    };

    // Lleva el historial del registro al listado como filtro
    const filtrarPorRegistro = async ({ tabla, registro_id }) => {
        cerrarHistorial();
        cerrarDetalle();
        filtros.value = { ...filtrosVacios(), tabla, registro_id };
        await applyFilters();
    };

    const iniciar = async () => {
        await Promise.all([getAdmins(), getTablas(), getAuditoria(1)]);
    };

    const headers = Object.freeze([
        { field: "created_at", label: "Fecha" },
        { field: "admin", label: "Admin" },
        { field: "impersonado", label: "Como usuario" },
        { field: "accion", label: "Acción" },
        { field: "status_http", label: "Resultado" },
        { field: "total_cambios", label: "Cambios" },
        { field: "duracion_ms", label: "Duración" },
        { field: "actions", label: "" },
    ]);

    const auditoria = computed(() => store.state.appPlataforma.auditoria);
    const admins = computed(() => store.state.appPlataforma.admins);
    const tablas = computed(() => store.state.appPlataforma.tablas);

    return {
        headers,
        auditoria,
        admins,
        tablas,
        spinner,
        exportando,
        sinAcceso,
        alertTable,
        filtros,
        modalDetalle,
        detalle,
        cargandoDetalle,
        modalHistorial,
        historial,
        cargandoHistorial,
        iniciar,
        getAuditoria,
        applyFilters,
        cleanFilter,
        pageChangeHandle,
        exportar,
        verDetalle,
        cerrarDetalle,
        verHistorial,
        cerrarHistorial,
        filtrarPorRegistro,
    };
}
